// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { RefObject, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ClearMapPickToRevealAction } from './projectListActions';
import { IMountedInChunks } from './useMountedInChunks';

interface IRevealSelectedProjectOptions {
  // The element holding the grid's cards or rows, so the picked one is found inside it.
  listRef: RefObject<HTMLElement>;
  // The projects in grid order, so the pick can be found by index.
  projects: { _id: string }[];
  // How the list mounts its items; a list that renders every item at once (a folder's page)
  // passes nothing, and every index counts as mounted.
  mounted?: IMountedInChunks;
  // The sort the list is in, when it is not the store's (a folder's page sorts locally), so a
  // sort change there re-centres like the dashboard's does.
  sortKey?: string;
}

// AUT-6432: picking a project on the map and closing the map lands the user on that project -
// the grid scrolls its row to the middle, where the row's own isSelected outlines it. The pick
// to land on is set by the map's close (mapPickToReveal) and cleared when the map reopens or
// the dashboard is left, so a later visit does not scroll back to it. While it stands: once
// per pick - the user scrolling away from the row is not dragged back to it - but a sort or a
// new set landing re-centres, so changing the sort afterwards keeps them on it.
//
// A pick past the mounted depth is mounted first; the effect re-runs once mountedCount grows
// and then scrolls. Optional call: jsdom has no scrollIntoView.
//
// The folder page lands the same way: leaving the map inside a folder goes to that folder's
// page, which reads the same pick.
export const useRevealSelectedProject = ({ listRef, projects, mounted, sortKey }: IRevealSelectedProjectOptions) => {
  const dispatch = useAppDispatch();
  const mapPickToReveal = useAppSelector((state) => state.projectList.mapPickToReveal);
  const storeSortFilter = useAppSelector((state) => state.projectList.sortFilter);
  const sortFilter = sortKey ?? storeSortFilter;
  const mapCoordinatesKey = useAppSelector((state) => state.projectList.mapCoordinatesKey);
  const mountedCount = mounted ? mounted.mountedCount : projects.length;
  const ensureMounted = mounted ? mounted.ensureMounted : () => true;
  const lastRevealedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!mapPickToReveal) {
      // No landing under way (the map is open again): the next close starts afresh, even for
      // the same pick.
      lastRevealedRef.current = null;
      return;
    }
    const revealContext = `${mapPickToReveal}|${sortFilter}|${mapCoordinatesKey}`;
    if (lastRevealedRef.current === revealContext) return;
    const selectedIndex = projects.findIndex((project) => project._id === mapPickToReveal);
    if (selectedIndex === -1) return;
    if (!ensureMounted(selectedIndex)) return;
    const card = listRef.current?.querySelector(`[data-project-id="${mapPickToReveal}"]`);
    // Not on screen yet (a list still behind its loading state): the landing waits for it
    if (!card) return;
    lastRevealedRef.current = revealContext;
    card.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [mapPickToReveal, sortFilter, mapCoordinatesKey, projects, mountedCount]);

  // Leaving ends the landing once it has happened; the pick itself stays selected for the map.
  // A pick not yet landed on survives the leave for the page that will land on it: leaving the
  // map inside a folder unmounts the dashboard on its way to the folder's page.
  useEffect(() => () => {
    if (lastRevealedRef.current) dispatch(ClearMapPickToRevealAction());
  }, []);
};

export default useRevealSelectedProject;
